import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ButtonsOptions, ColorOptions, HorizontalAlignmentOptions, HtmlTagOptions, LayoutWidthOptions } from '@df/ui/atomic/atomic-options';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import range from 'lodash-es/range';

export namespace NTextComponent {
    export interface IComponentContent {
        type: 'text' | 'button';
        content_width?: number;
        align?: string;
        text?: string;
        cta_label?: string;
        cta_link?: string;
        button_class?: string;
        form_size?: string;
        text_style?: string;
        color?: string;
        tag?: string;
        column_align?: string;
        section_width?: string;
    }
}

@Component({
    selector: 'page-component-text',
    templateUrl: './text.html',
    standalone: true,
    imports: [NgClass, ICmsClientModule, HrefComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<PageComponentTextComponent>({
    id: 'text',
    name: 'Text',
    image: '/common/cms/text-component.png',
    tags: ['text', 'heading'],
    fields: [
        // general
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-general',
            title: 'General'
        },
        <NICmsContentField.IChoiceField>{
            key: 'type',
            type: NICmsContentField.EFieldType.choice,
            title: 'Type',
            options: [
                { key: 'text', title: 'Text Line' },
                { key: 'button', title: 'Button' }
            ],
            default: 'text'
        },
        <NICmsContentField.IChoiceField>{
            key: 'button_class',
            type: NICmsContentField.EFieldType.choice,
            title: 'Button Style',
            options: ButtonsOptions,
            default: ButtonsOptions[0] ? ButtonsOptions[0].key : undefined,
            canShow: (data: NTextComponent.IComponentContent) => data?.type === 'button'
        },
        {
            key: 'cta_label',
            type: NICmsContentField.EFieldType.text,
            title: 'Button Label',
            canShow: (data: NTextComponent.IComponentContent) => data?.type === 'button'
        },
        {
            key: 'cta_link',
            type: NICmsContentField.EFieldType.text,
            title: 'Link',
            canShow: (data: NTextComponent.IComponentContent) => data?.type === 'button'
        },
        <NICmsContentField.IChoiceField>{
            key: 'content_width',
            type: NICmsContentField.EFieldType.choice,
            title: 'Content Width',
            itemName: 'content_width',
            options: () => range(1, 13).map(i => ({ key: i, title: i + '' })),
            default: 12,
            canShow: (data: NTextComponent.IComponentContent) => data?.type !== 'button'
        },
        {
            key: 'text',
            type: NICmsContentField.EFieldType.textarea,
            title: 'Copy',
            description: 'Text to display. HTML code can be used.',
            default: 'Lorem ipsum dolor',
            required: true,
            canShow: (data: NTextComponent.IComponentContent) => data?.type !== 'button'
        },
        <NICmsContentField.IChoiceField>{
            key: 'align',
            type: NICmsContentField.EFieldType.choice,
            title: 'Text Alignment',
            options: HorizontalAlignmentOptions,
            default: 'center'
        },
        <NICmsContentField.IChoiceField>{
            key: 'color',
            type: NICmsContentField.EFieldType.choice,
            previewMode: 'color',
            title: 'Text Color',
            options: ColorOptions,
            default: '',
            canShow: (data: NTextComponent.IComponentContent) => data?.type !== 'button' || data?.button_class === 'button-inline'
        },
        <NICmsContentField.IChoiceField>{
            key: 'tag',
            type: NICmsContentField.EFieldType.choice,
            title: 'Force Tag',
            description: 'For SEO reasons you may want to force particular HTML tag',
            options: HtmlTagOptions,
            default: '',
            canShow: (data: NTextComponent.IComponentContent) => data?.type !== 'button'
        },
        <NICmsContentField.IChoiceField>{
            key: 'section_width',
            type: NICmsContentField.EFieldType.choice,
            title: 'Section Width',
            itemName: 'section_width',
            options: LayoutWidthOptions,
            default: undefined
        },
        {
            key: 'column_align',
            type: NICmsContentField.EFieldType.choice,
            title: 'Column Alignment',
            options: HorizontalAlignmentOptions,
            default: undefined
        }
    ]
})
export class PageComponentTextComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'component'
    })
    component!: NTextComponent.IComponentContent;
}
